import type { CardRecord, PreferenceRecord, SavedPlanRecord } from "@music-app/persistence";

/** Card row shape as returned by Supabase (snake_case column names). */
export interface RemoteCard {
  item_id: string;
  card: unknown;
  updated_at: string;
}

/** Preference row shape as returned by Supabase. */
export interface RemotePref {
  key: string;
  value: unknown;
  updated_at: string;
}

/** Preference record with the optional timestamp guaranteed present. */
export type PreferenceRecordWithTs = PreferenceRecord & { updatedAt: string };

/** Saved plan row as returned by Supabase (snake_case). */
export interface RemotePlan {
  id: string;
  name: string;
  source: string;
  generator_id: string;
  session_mix: string;
  focus_tracks: unknown;
  focus_node_ids: unknown;
  config_overrides: unknown;
  generator_state: unknown;
  created_at: string;
  updated_at: string;
}

const EPOCH = "1970-01-01T00:00:00.000Z";

/**
 * Last-write-wins merge for FSRS cards. Both sides carry an ISO timestamp.
 * Returns:
 *   localUpdates  — records newer on the remote side; write into local Dexie.
 *   remoteUpdates — records newer on the local side; upsert into Supabase.
 */
export function mergeCards(
  local: CardRecord[],
  remote: RemoteCard[],
): { localUpdates: CardRecord[]; remoteUpdates: CardRecord[] } {
  const localMap = new Map(local.map((c) => [c.id, c]));
  const remoteMap = new Map(remote.map((c) => [c.item_id, c]));

  const localUpdates: CardRecord[] = [];
  const remoteUpdates: CardRecord[] = [];

  for (const r of remote) {
    const l = localMap.get(r.item_id);
    if (!l || r.updated_at > l.updatedAt) {
      localUpdates.push({
        id: r.item_id,
        card: r.card as CardRecord["card"],
        updatedAt: r.updated_at,
      });
    }
  }

  for (const l of local) {
    const r = remoteMap.get(l.id);
    if (!r || l.updatedAt >= r.updated_at) {
      remoteUpdates.push(l);
    }
  }

  return { localUpdates, remoteUpdates };
}

/**
 * Last-write-wins merge for user preferences.
 * Local records without an updatedAt are treated as epoch (very old) so
 * they lose to any remote value — which is correct for first sign-in where
 * the remote already has a more recent write.
 */
export function mergePrefs(
  local: (PreferenceRecord & { updatedAt?: string })[],
  remote: RemotePref[],
): { localUpdates: PreferenceRecordWithTs[]; remoteUpdates: PreferenceRecordWithTs[] } {
  const localMap = new Map(local.map((p) => [p.key, p]));
  const remoteMap = new Map(remote.map((p) => [p.key, p]));

  const localUpdates: PreferenceRecordWithTs[] = [];
  const remoteUpdates: PreferenceRecordWithTs[] = [];

  for (const r of remote) {
    const l = localMap.get(r.key);
    if (!l || r.updated_at > (l.updatedAt ?? EPOCH)) {
      localUpdates.push({ key: r.key, value: r.value, updatedAt: r.updated_at });
    }
  }

  for (const l of local) {
    const r = remoteMap.get(l.key);
    const localAt = l.updatedAt ?? EPOCH;
    if (!r || localAt >= r.updated_at) {
      remoteUpdates.push({ key: l.key, value: l.value, updatedAt: localAt });
    }
  }

  return { localUpdates, remoteUpdates };
}

/**
 * Last-write-wins merge for saved plans. Same pattern as mergeCards.
 * Only syncs user-created plans (source === "user"); app plans are code constants.
 */
export function mergePlans(
  local: SavedPlanRecord[],
  remote: RemotePlan[],
): { localUpdates: SavedPlanRecord[]; remoteUpdates: SavedPlanRecord[] } {
  const localMap = new Map(local.map((p) => [p.id, p]));
  const remoteMap = new Map(remote.map((p) => [p.id, p]));

  const localUpdates: SavedPlanRecord[] = [];
  const remoteUpdates: SavedPlanRecord[] = [];

  for (const r of remote) {
    const l = localMap.get(r.id);
    if (!l || r.updated_at > l.updatedAt) {
      localUpdates.push({
        id: r.id,
        name: r.name,
        source: r.source as "app" | "user",
        generatorId: r.generator_id,
        sessionMix: r.session_mix as SavedPlanRecord["sessionMix"],
        focusTracks: (r.focus_tracks ?? null) as string[] | null,
        focusNodeIds: (r.focus_node_ids ?? null) as string[] | null,
        configOverrides: (r.config_overrides ?? null) as Record<string, unknown> | null,
        generatorState: (r.generator_state ?? null) as Record<string, unknown> | null,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
      });
    }
  }

  for (const l of local) {
    const r = remoteMap.get(l.id);
    if (!r || l.updatedAt >= r.updated_at) {
      remoteUpdates.push(l);
    }
  }

  return { localUpdates, remoteUpdates };
}
